<script setup>
import { driver } from 'driver.js';
import i18next from 'i18next';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import TutorialBanner from '../components/TutorialBanner.vue';
import Calendar from '../components/weeklyCalendar/Calendar.vue';
import { addDays, getTodayDate, subtractDays } from '../components/weeklyCalendar/utils';
import { fetchData } from '../fetchData';

const props = defineProps({
    currentProductId: {
        type: String,
        default: '',
        required: true
    }
});
const router = useRouter();
const calendarContainer = ref(null);

const API = `/api/v1/`;
const activeCalendar = ref('week');
const errorMessage = ref(null);

const initWeekList = ref([]);
const weeklyStartDate = ref(getTodayDate());

const initEarningList = ref([]);
const earningStartDate = ref(getTodayDate());
const earningDirection = ref('future');

const startCalendarTour = async () => {
    activeCalendar.value = 'week';
    await nextTick();

    if (!calendarContainer.value) {
        console.error('calendarContainer is not defined');

        return;
    }

    const steps = [];

    if (initWeekList.value.length > 0) {
        steps.push({
            element: calendarContainer.value,
            popover: {
                title: i18next.t('calendar.analysis_calendar.title'),
                description: i18next.t('calendar.analysis_calendar.description'),
                side: 'bottom',
                align: 'start'
            }
        });

        steps.push({
            element: calendarContainer.value,
            popover: {
                title: i18next.t('calendar.hover_effect.title'),
                description: i18next.t('calendar.hover_effect.description'),
                side: 'top',
                align: 'start'
            }
        });

        steps.push({
            element: calendarContainer.value,
            popover: {
                title: i18next.t('calendar.latest_analysis.title'),
                description: i18next.t('calendar.latest_analysis.description'),
                side: 'top',
                align: 'start'
            },
            onHighlightStarted: () => {
                activeCalendar.value = 'week';
            }
        });
    }

    if (initEarningList.value.length > 0) {
        steps.push({
            element: calendarContainer.value,
            popover: {
                title: i18next.t('calendar.earnings_calendar.title'),
                description: i18next.t('calendar.earnings_calendar.description'),
                side: 'top',
                align: 'start'
            },
            onHighlightStarted: () => {
                activeCalendar.value = 'earning';
            }
        });
    }

    if (steps.length > 0) {
        steps.push({
            popover: {
                title: i18next.t('calendar.support.title'),
                description: i18next.t('calendar.support.description'),
                side: 'top',
                align: 'start'
            }
        });

        steps.push({
            popover: {
                title: i18next.t('calendar.signoff.description'),
                side: 'top',
                align: 'start'
            }
        });
    }

    const driverObj = driver({
        allowHtml: true,
        showProgress: true,
        nextBtnText: i18next.t('plain_next'),
        prevBtnText: i18next.t('plain_back'),
        doneBtnText: i18next.t('plain_done'),
        steps
    });

    driverObj.drive();
};
const getWeeklyCalenderData = async () => {
    try {
        const url = `${API}analysis-calendar/${props.currentProductId}?startDate=${weeklyStartDate.value}`;
        const { result = [], error = null } = await fetchData(url);
        initWeekList.value = result;

        errorMessage.value = error;
    } catch (e) {
        console.error('Error fetching data:', e);
        errorMessage.value = 'Es gab einen Fehler beim Laden der Daten.';
    }
};

const getEarningCalendarData = async () => {
    try {
        const url = `${API}earnings-calendar/${props.currentProductId}?direction=${earningDirection.value}&startDate=${earningStartDate.value}`;
        const { result = [], error = null } = await fetchData(url);
        initEarningList.value = result;

        errorMessage.value = error;
    } catch (e) {
        console.error('Error fetching data:', e);
        errorMessage.value = 'Es gab einen Fehler beim Laden der Daten.';
    }
};

const handleShowTitle = (id) => {
    router.push(`/dashboard/markets-analysis/${id}`);
};

const loadMorePastCalendarItems = async () => {
    weeklyStartDate.value = subtractDays(initWeekList.value[0]?.date, 7);
    await getWeeklyCalenderData();
};
const updateCalenderDays = async () => {
    weeklyStartDate.value = addDays(initWeekList.value[0]?.date, 7);
    await getWeeklyCalenderData();
};
const updateEarningItems = async () => {
    earningDirection.value = 'future';
    earningStartDate.value = initEarningList.value[initEarningList.value.length - 1]?.date;
    await getEarningCalendarData();
};
const loadMorePastEarningItems = async () => {
    earningDirection.value = 'past';
    earningStartDate.value = initEarningList.value[0]?.date;

    await getEarningCalendarData();
};
const hasWeekList = computed(() => initWeekList.value.length > 0);

const showWCalenderPastButton = computed(() => weeklyStartDate.value !== getTodayDate());

const showEarningPastButton = computed(() => {
    return earningDirection.value === 'future' || (earningDirection.value === 'past' && initEarningList.value.length >= 7);
});
const showEarningFutureButton = computed(() => {
    return earningDirection.value === 'past' || (earningDirection.value === 'future' && initEarningList.value.length >= 7);
});

onMounted(async () => {
    await getWeeklyCalenderData();
    await getEarningCalendarData();

    if (initWeekList.value.length === 0 && initEarningList.value.length > 0) {
        activeCalendar.value = 'earning';
    }
});
</script>

<template>
    <div v-if="initWeekList.length > 0 || initEarningList.length > 0">
        <TutorialBanner :title="$t('calendar.new_feature')" :show-until="'2025-10-01'" class="mb-4" @start-tutorial="startCalendarTour" />

        <div ref="calendarContainer">
            <div class="flex justify-between">
                <div class="flex gap-2">
                    <button
                        v-if="initWeekList.length > 0"
                        class="px-4 py-2 border rounded-lg text-blue border-blue hover:bg-blue/70 hover:text-white transition duration-300"
                        :class="{ 'bg-blue text-white': activeCalendar === 'week' }"
                        @click="activeCalendar = 'week'"
                    >
                        {{ $t('analyse_calendar') }}
                    </button>
                    <button
                        v-if="initEarningList.length > 0"
                        class="px-4 py-2 border rounded-lg text-blue border-blue hover:bg-blue/70 hover:text-white transition duration-300"
                        :class="{ 'bg-blue text-white': activeCalendar === 'earning' }"
                        @click="activeCalendar = 'earning'"
                    >
                        {{ $t('earning_calendar') }}
                    </button>
                </div>
                <info-text
                    class="hidden sm:block"
                    :title="activeCalendar === 'week' ? $t('analyse_calendar') : $t('earning_calendar')"
                    :text="activeCalendar === 'week' ? $t('analyse_calendar_description') : $t('earning_calendar_description')"
                />
            </div>

            <div v-if="activeCalendar === 'week' && hasWeekList">
                <Calendar
                    :init-week="initWeekList"
                    :show-past-items="showWCalenderPastButton"
                    @handle-stream="handleShowTitle"
                    @load-more-items="updateCalenderDays"
                    @load-more-past-items="loadMorePastCalendarItems"
                />
            </div>
            <div v-if="activeCalendar === 'earning' && initEarningList.length > 0">
                <Calendar
                    :init-week="initEarningList"
                    :show-past-items="showEarningPastButton"
                    :show-future-items="showEarningFutureButton"
                    @handle-stream="handleShowTitle"
                    @load-more-items="updateEarningItems"
                    @load-more-past-items="loadMorePastEarningItems"
                />
            </div>
        </div>
    </div>

    <div class="mt-2 text-center text-sm text-gray-500">
        <span v-if="activeCalendar === 'earning'" class="text-xxs"
            >*
            {{ $t('earnings_footnote_before') }}
            <a href="/hkcmnews" target="_blank" class="text-blue hover:underline text-xs font-bold"> {{ $t('earnings_footnote_link') }} </a>{{ $t('earnings_footnote_after') }}
        </span>
    </div>
    <div v-if="errorMessage" class="text-red text-center mt-4">
        {{ errorMessage }}
    </div>
</template>
